import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import { dateRangePresetEnum } from '@/stores/portal.store'

dayjs.extend(isoWeek)

// Preset values live in `dateRangePresetEnum` (portal.store); this holds the
// range-resolution logic shared by the DateRangeNavigator and its consumers.

// `labelCount` null → call t(key) with no count. Only `selectable` presets can
// be saved as a default (CUSTOM cannot).
export const dateRangePresetDefinitions = Object.freeze([
  { value: dateRangePresetEnum.WEEK, labelKey: 'common.week', labelCount: 1, icon: 'mdi-calendar-week', selectable: true },
  { value: dateRangePresetEnum.TWO_WEEKS, labelKey: 'common.twoWeeks', labelCount: null, icon: 'mdi-calendar-range', selectable: true },
  { value: dateRangePresetEnum.THIRTY_DAYS, labelKey: 'common.thirtyDays', labelCount: null, icon: 'mdi-calendar-range', selectable: true },
  { value: dateRangePresetEnum.MONTH, labelKey: 'common.month', labelCount: 1, icon: 'mdi-calendar-month', selectable: true },
  { value: dateRangePresetEnum.QUARTER, labelKey: 'common.quarter', labelCount: 1, icon: 'mdi-calendar-multiselect', selectable: true },
  { value: dateRangePresetEnum.SEMESTER, labelKey: 'common.semester', labelCount: 1, icon: 'mdi-calendar-star', selectable: true },
  { value: dateRangePresetEnum.YEAR, labelKey: 'common.year', labelCount: 1, icon: 'mdi-calendar-today', selectable: true },
  { value: dateRangePresetEnum.CUSTOM, labelKey: 'common.custom', labelCount: null, icon: 'mdi-calendar-edit', selectable: false },
])

// The calendar-snapped presets, for selectors that only offer whole periods.
export const calendarPresets = Object.freeze([
  dateRangePresetEnum.WEEK,
  dateRangePresetEnum.MONTH,
  dateRangePresetEnum.YEAR,
])

export const clampToPresets = (preset, allowed, fallback = allowed?.[0]) => (
  allowed?.includes(preset) ? preset : fallback ?? preset
)

// A saved period has no CUSTOM value: a hand-picked range is stored as `null`.
export const savedPreset = (preset) => (preset === dateRangePresetEnum.CUSTOM ? null : preset)
export const presetOrCustom = (preset) => preset ?? dateRangePresetEnum.CUSTOM

// Day-span view labels shared by the event scheduler and the warehouse
// availability day selector, so the two stay uniform ("3 days"/"work week"/"week").
export const WORK_WEEK_DAYS = 5
export const WEEK_DAYS = 7

export const availabilityDaySpans = Object.freeze([3, WORK_WEEK_DAYS, WEEK_DAYS])

export const daySpanViewLabel = (t, days) => {
  switch (days) {
    case WORK_WEEK_DAYS:
      return t('common.workWeek')
    case WEEK_DAYS:
      return t('common.week')
    default:
      return `${days} ${t('common.day', days)}`
  }
}

// Quarter helpers: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec
export const quarterStart = (d) => d.startOf('month').month(Math.floor(d.month() / 3) * 3)
export const quarterEnd = (d) => quarterStart(d).add(2, 'month').endOf('month')

// Semester helpers: H1=Jan-Jun, H2=Jul-Dec
export const semesterStart = (d) => d.startOf('month').month(d.month() < 6 ? 0 : 6)
export const semesterEnd = (d) => semesterStart(d).add(5, 'month').endOf('month')

// { from, to } dayjs pair for a preset (null for custom/unknown). The
// duration presets span the period both backwards and forwards around `now`
// (e.g. "2 weeks" = 2 weeks back + 2 weeks forward); the calendar presets snap
// to their boundaries, already covering the current period's past days.
export const rangeForPreset = (preset, base = dayjs()) => {
  const now = base
  switch (preset) {
    case dateRangePresetEnum.WEEK:
      return { from: now.startOf('isoWeek'), to: now.endOf('isoWeek') }
    case dateRangePresetEnum.TWO_WEEKS:
      return { from: now.subtract(14, 'day'), to: now.add(14, 'day') }
    case dateRangePresetEnum.THIRTY_DAYS:
      return { from: now.subtract(30, 'day'), to: now.add(30, 'day') }
    case dateRangePresetEnum.MONTH:
      return { from: now.startOf('month'), to: now.endOf('month') }
    case dateRangePresetEnum.QUARTER:
      return { from: quarterStart(now), to: quarterEnd(now) }
    case dateRangePresetEnum.SEMESTER:
      return { from: semesterStart(now), to: semesterEnd(now) }
    case dateRangePresetEnum.YEAR:
      return { from: now.startOf('year'), to: now.endOf('year') }
    default:
      return null
  }
}

export const isoRangeForPreset = (preset, base = dayjs()) => {
  const range = rangeForPreset(preset, base)
  return range && { from: range.from.format('YYYY-MM-DD'), to: range.to.format('YYYY-MM-DD') }
}
